import { useState, ChangeEvent, useEffect, SyntheticEvent } from 'react';
import {
  Grid, Card, Button, Divider, CardContent, MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import CardActions from '@mui/material/CardActions';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Title from '../../../components/Title';
import axios from "axios";
import { Role } from '../../../models/role';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import SelectUserCollectsByOffice from '../../../components/filter/SelectMultipleCollectsByOffice';
import { useDrawer } from '../../../components/context/DrawerContext';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}
interface State {
  password: string;
  password2: string;
  showPassword: boolean;
  showPassword2: boolean;
}
const UserEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [updatePassword, setUpdatePassword] = useState(false); // Estado para el Switch
  const { drawerOpen } = useDrawer();

  const [firstname, setFirst_name] = useState('');
  const [lastname, setLast_name] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [idnumber, setIdnumber] = useState('');
  const [role_id, setRole_id] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [officeCollects, setOfficeCollects] = useState<{ office_id: string; selectedCollects: string[] }[]>([]);
  const [collectsByOffice, setCollectsByOffice] = useState<Record<string, Collect[]>>({});
  const [password, setPassword] = useState('');
  const [password_confirm, setPassword_confirm] = useState('');
  const [isModified, setIsModified] = useState(false); // Estado 
  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const [resRoles, resOffices, resCollects, resUser] = await Promise.all([
          axios.get('roles'),
          axios.get('alloffices'),
          axios.get('allcollects'),
          axios.get(`users/${id}`)
        ]);

        setRoles(resRoles.data);
        setOffices(resOffices.data.data);

        const groupByOffice: Record<string, Collect[]> = {};
        resCollects.data.data.forEach((collect: Collect) => {
          const officeId = collect.office.id.toString();
          if (!groupByOffice[officeId]) groupByOffice[officeId] = [];
          groupByOffice[officeId].push(collect);
        });
        setCollectsByOffice(groupByOffice);

        const user = resUser.data;
        setFirst_name(user.first_name);
        setLast_name(user.last_name);
        setUsername(user.username);
        setIdnumber(user.idnumber);
        setEmail(user.email);
        setRole_id(user.role.id);
        setOfficeCollects(user.offices.map((entry: any) => ({
          office_id: entry.office_id.toString(),
          selectedCollects: entry.collect_id.map((id: number) => id.toString())
        })));

        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulación de carga
        showAlert('Datos cargados exitosamente', 'success');
      } catch (error) {
        console.error('Error fetching data:', error);
        showAlert('Error al cargar los datos', 'error');
      } finally {
        hideLoading();
      }
    })();
  }, [id]);

  const handleOfficeCollectsChange = (data: { office_id: string; selectedCollects: string[] }[]) => {
    setOfficeCollects(data);
  };
  const handleUpdatePasswordChange = () => {
    setUpdatePassword(!updatePassword);
    setPassword('');
    setPassword_confirm('');
    setIsModified(true);
  };
  const [values, setValues] = useState<State>({
    password: '',
    password2: '',
    showPassword: false,
    showPassword2: false,
  });
  // Handle Password
  const handlePasswordChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
    setIsModified(true);
  };
  // Handle Confirm Password
  const handleConfirmChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
    setIsModified(true);
  };
  const isFormIncomplete = officeCollects.some(
    (entry) => !entry.office_id || entry.selectedCollects.length === 0
  );
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!firstname || !lastname || !username || !role_id || officeCollects.length === 0) {
      showAlert('Tiene campos pendientes por diligenciar', 'warning');
      return;
    }

    if (updatePassword && values.password !== values.password2) {
      showAlert('Las contraseñas no coinciden', 'warning');
      return;
    }

    showLoading();
    const formData = {
      firstname,
      lastname,
      idnumber,
      username,
      role_id,
      password: updatePassword ? values.password : undefined,
      offices: officeCollects.map(item => ({
        office_id: parseInt(item.office_id),
        collect_id: item.selectedCollects.map(id => parseInt(id))
      }))
    };
    try {
      await axios.put(`users/${id}`, formData);
      showAlert('Se ha actualizado el usuario exitosamente!', 'success');
      navigate('/administrar/usuarios');
    } catch (error) {
      console.error('Error actualizando el usuario:', error);
      showAlert('Error actualizando el usuario', 'error');
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Editar Usuario</Title>
      </StickyListToolbar>
      <Card>
        <form onSubmit={submit}>
          <CardContent
            sx={{
              width: (theme) => stickyListRegionWidth(theme, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Cédula" value={idnumber} onChange={e => setIdnumber(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Usuario" value={username} onChange={e => setUsername(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Nombres" value={firstname} onChange={e => setFirst_name(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size='small' label="Apellidos" value={lastname} onChange={e => setLast_name(e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="Rol">Seleccionar Perfil</InputLabel>
                  <Select labelId="Rol" size='small' value={role_id} onChange={e => setRole_id(e.target.value)}>
                    {roles.map((role: Role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={updatePassword}
                      onChange={handleUpdatePasswordChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  }
                  label="Actualizar Contraseña"
                />
              </Grid>
              {updatePassword && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size='small'
                        label="Contraseña"
                        type={values.showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        onChange={handlePasswordChange('password')}
                        value={values.password}
                        inputProps={{ maxLength: 30, autoComplete: "off" }}
                        required
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size='small'
                          label="Confirmar Contraseña"
                          type={values.showPassword2 ? 'text' : 'password'}
                          placeholder="Confirmar Contraseña"
                          onChange={handleConfirmChange('password2')}
                          value={values.password2}
                          inputProps={{ maxLength: 30, autoComplete: "off" }}
                          required
                          autoComplete="off"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 1 }} />
            <SelectUserCollectsByOffice
              key={JSON.stringify(officeCollects)} // 👈 Forzar remount
              offices={offices}
              collectsByOffice={collectsByOffice}
              onChange={handleOfficeCollectsChange}
              initialSelections={officeCollects}
            />

            <Divider sx={{ margin: 1 }} />
            <CardActions>
              <Button size='small' type="submit" sx={{ mr: 2 }} variant="contained" disabled={isFormIncomplete} >Guardar</Button>
              <Link to="/administrar/usuarios"><Button size='small' color="secondary" variant="outlined">Cancelar</Button></Link>
            </CardActions>
          </CardContent>

        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default UserEdit;